import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon } from '../components/Icons';
import { GlassCard } from '../components/GlassCard';
import { api } from '../lib/api';
import { LogEntrySkeleton } from '../components/Skeleton';
import { T } from '../lib/design-tokens';
import type { QueryLogRecord } from '../types';

const LIMIT = 50;

const TYPE_LABELS: Record<string, string> = {
  user_input: 'Input',
  classification: 'Classification',
  plan: 'Plan',
  approval: 'Approval',
  execution: 'Execution',
  result: 'Result',
  info_retrieval: 'Info',
  system_message: 'System',
};

const TYPE_COLORS: Record<string, string> = {
  user_input: T.cream35,
  classification: T.gold,
  plan: T.amber,
  approval: T.green,
  execution: T.gold,
  result: T.green,
  info_retrieval: T.cream60,
  system_message: T.cream35,
};

function formatTimestamp(ts: string) {
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ', ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function getPrimaryText(log: QueryLogRecord): string {
  if (log.messageType === 'info_retrieval' && log.infoResponse) {
    return log.infoResponse.length > 100 ? log.infoResponse.slice(0, 100) + '...' : log.infoResponse;
  }
  if (log.inputText) return log.inputText;
  return log.action || 'Log entry';
}

export function HistoryPage() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<QueryLogRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Search
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<QueryLogRecord[] | null>(null);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchLogs = useCallback(async (loadOffset: number, append: boolean) => {
    try {
      const data = await api<{ logs: QueryLogRecord[]; limit: number; offset: number }>(
        `/history?limit=${LIMIT}&offset=${loadOffset}`,
      );
      setLogs((prev) => (append ? [...prev, ...data.logs] : data.logs));
      setHasMore(data.logs.length >= LIMIT);
      setOffset(loadOffset + data.logs.length);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(0, false);
  }, [fetchLogs]);

  const handleLoadMore = () => {
    setLoadingMore(true);
    fetchLogs(offset, true);
  };

  const handleSearch = useCallback(async (q: string) => {
    if (!q.trim()) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const data = await api<{ logs: QueryLogRecord[] }>(
        `/history/search?q=${encodeURIComponent(q.trim())}`,
      );
      setSearchResults(data.logs);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleQueryChange = (val: string) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(val), 300);
  };

  const clearSearch = () => {
    setQuery('');
    setSearchResults(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
  };

  const displayLogs = searchResults !== null ? searchResults : logs;

  return (
    <div className="max-w-[760px] mx-auto" style={{ padding: '48px 40px' }}>
      <h1
        style={{
          fontFamily: T.serif,
          fontSize: 30,
          fontWeight: 400,
          color: T.cream,
          marginBottom: 24,
          letterSpacing: '-0.5px',
          animation: 'fadeUp 0.5s ease both',
        }}
      >
        History
      </h1>

      <div
        className="flex items-center gap-3 mb-8"
        style={{
          padding: '12px 16px',
          borderRadius: 12,
          border: `1.5px solid ${T.glassBorder}`,
          background: T.glass,
          transition: 'border-color 0.2s',
        }}
        onFocus={(e) => { e.currentTarget.style.borderColor = T.gold; }}
        onBlur={(e) => { e.currentTarget.style.borderColor = T.glassBorder; }}
      >
        <span className="shrink-0" style={{ color: T.cream35, display: 'flex' }}>
          <SearchIcon size={16} />
        </span>
        <input
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          placeholder="Search your history..."
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            fontFamily: T.sans,
            fontSize: 14,
            color: T.cream,
            background: 'transparent',
          }}
        />
        {query && (
          <button
            type="button"
            onClick={clearSearch}
            className="border-none bg-transparent cursor-pointer p-0"
            style={{ color: T.cream35, fontSize: 16 }}
          >
            &times;
          </button>
        )}
      </div>

      {loading && !searchResults && (
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <LogEntrySkeleton key={i} />
          ))}
        </div>
      )}

      {searching && (
        <div style={{ fontFamily: T.sans, fontSize: 14, padding: '16px 0', textAlign: 'center', color: T.cream35 }}>
          Searching...
        </div>
      )}

      {!loading && !searching && displayLogs.length === 0 && (
        <GlassCard style={{ padding: 64, textAlign: 'center' }}>
          <div style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 500, color: T.cream35, marginBottom: 8 }}>
            {searchResults !== null ? `No results for "${query}"` : 'No history yet'}
          </div>
          {searchResults === null && (
            <div style={{ fontFamily: T.sans, fontSize: 12, color: T.cream20 }}>
              Your task history will appear here.
            </div>
          )}
        </GlassCard>
      )}

      {!loading && displayLogs.length > 0 && (
        <div className="flex flex-col gap-2">
          {displayLogs.map((log, i) => {
            const color = TYPE_COLORS[log.messageType] ?? T.cream35;
            const label = TYPE_LABELS[log.messageType] ?? log.messageType;
            const isClickable = !!log.taskId;

            return (
              <GlassCard
                key={log.id}
                hover={isClickable}
                onClick={isClickable ? () => navigate(`/operations/${log.taskId}`) : undefined}
                style={{
                  padding: '14px 18px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 14,
                  cursor: isClickable ? 'pointer' : 'default',
                  animation: `fadeUp 0.4s ease ${i * 0.04}s both`,
                }}
              >
                <span
                  className="shrink-0 text-[10px] font-semibold uppercase mt-0.5"
                  style={{
                    padding: '3px 8px',
                    borderRadius: 6,
                    background: T.cream08,
                    color,
                    letterSpacing: '0.04em',
                    fontFamily: T.sans,
                  }}
                >
                  {label}
                </span>
                <div className="flex-1 min-w-0">
                  <div
                    className="truncate"
                    style={{ fontSize: 13.5, color: T.cream, fontWeight: 500, fontFamily: T.sans }}
                  >
                    {getPrimaryText(log)}
                  </div>
                  <div className="mt-1" style={{ fontSize: 12.5, color: T.cream35, fontFamily: T.sans }}>
                    {log.action ? log.action.replace(/_/g, ' ') : label} &middot; {formatTimestamp(log.timestamp)}
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}

      {!loading && searchResults === null && hasMore && (
        <div className="text-center mt-6">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={loadingMore}
            style={{
              padding: '9px 24px',
              borderRadius: 10,
              border: `1px solid ${T.cream08}`,
              background: 'transparent',
              color: T.cream60,
              fontSize: 13,
              fontWeight: 500,
              cursor: loadingMore ? 'default' : 'pointer',
              fontFamily: T.sans,
            }}
          >
            {loadingMore ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
